import { useEffect, useState } from 'react'
import { IDigitalAddress } from '../../../../types/contact';
import { Table, Input } from 'reactstrap';

import { EditIcon, CheckMarkIcon, CancelIcon } from '../../../common/icons';
import { CheckGroup } from '../../../../hook/useCheckGroup';
import useToggle from '../../../../hook/useToggle';
import DigitalAddressUpdate from './digital-address-update';
import { GET_ORG_DIGITAL_ADDRESSES, SET_ORG_PRIMARY_DIGITAL_ADDRESS } from '../../../../graphql/contact-gql';
import { useLazyQuery, useMutation } from '@apollo/client';
import SpinnerOverlay from '../../../common/spinner/spinner';
import { notifySuccess } from '../../../../services/notification';
import { IOrg } from '../../../../types/org';
import ImgButton from '../../../common/img-button/img-button';
import useAuth from '../../../../hook/useAuth';
import { useFrench } from '../../../french/french-context';

type DigitalAddressProps = {
    org: IOrg;
    checkGroup: CheckGroup<number>;
}

const OrgDigitalAddress = (props: DigitalAddressProps) => {
    const {org, checkGroup} = props;
    const { isFrenchTranslator } = useAuth()
    const [frenchState] = useFrench()
    const [digitalAddress, setDigitalAddress] = useState<IDigitalAddress>()
    const {isOpen: editDigitalAddressOpen, toggle: toogleEditDigitalAddress} = useToggle()

    const [getOrgDigitalAddresses, {loading, data}] = useLazyQuery(GET_ORG_DIGITAL_ADDRESSES, {
        onCompleted: (data) => {
            if (data?.org?.digitalAddresses) {
                checkGroup.init(data.org.digitalAddresses.map((e: IDigitalAddress) => e.id))
            }
        }
    })

    const [setPrimaryAddress, {loading: loadingPrimary}] = useMutation(SET_ORG_PRIMARY_DIGITAL_ADDRESS)

    useEffect(() => {
        if (org) {
            getOrgDigitalAddresses({variables: { orgId: org.id },fetchPolicy:'network-only'})
        }
    }, [org])

    const editDigitalAddress = (digitalAddress: IDigitalAddress) => {
        setDigitalAddress(digitalAddress)
        toogleEditDigitalAddress()
    }

    const setPrimary = (digitalAddress: IDigitalAddress) => {
        setPrimaryAddress({
            variables: {
                orgId: org.id,
                digitalAddressId: digitalAddress.id
            },
            refetchQueries: [{
                query: GET_ORG_DIGITAL_ADDRESSES,
                variables: { orgId: digitalAddress?.businessEntityId }
            }],
            awaitRefetchQueries: true,
            onCompleted: (data) => data && notifySuccess()
        })
    }

    const getAddressTxt = (digitalAddress: IDigitalAddress) => {
        return digitalAddress.digitalType.code === "EMAIL"
            ? digitalAddress.emailAddressTxt
            : (digitalAddress.digitalType.code === "ENINTER" || digitalAddress.digitalType.code === "ENINTRA")
                ? digitalAddress.netAddressTxt
                : "No Address"
    }

    return (
        <div>
            {(loading || loadingPrimary) && <SpinnerOverlay />}

            {data?.org?.digitalAddresses &&
                <Table striped className="fixed" id="org_digital_addresses">
                    <thead>
                        <tr>
                            { !isFrenchTranslator && <th className="icon-column">
                                <span className="sr-only">select digital address</span>
                                {data.org.digitalAddresses.length > 0 && !checkGroup.isEmpty() &&
                                    <>
                                        <span id="orgDigitalSelectAllLbl" className="sr-only">Select all</span>
                                        <Input
                                            type="checkbox"
                                            title="Select all"
                                            aria-labelledby="orgDigitalSelectAllLbl"
                                            checked={checkGroup.isAllChecked()}
                                            onChange={checkGroup.onCheckAll}
                                            disabled={!!org.details?.shareLinkId}
                                        />
                                    </>
                                }
                            </th>}
                            <th>Digital Type</th>
                            <th>Digital Address</th>
                            <th className="primary-mailing-column">Primary</th>
                            <th style={{width: '25%'}}>Description</th>
                            { ( !isFrenchTranslator || frenchState.isFrenchChecked ) && <th className="edit-column">Edit</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {
                            data.org.digitalAddresses.length === 0
                            ?
                            <tr><td colSpan={!isFrenchTranslator? 6 : isFrenchTranslator && frenchState.isFrenchChecked? 5: 4} style={{textAlign: 'center'}}>No organization digital addresses found.</td></tr>
                            :
                            data.org.digitalAddresses?.map((digitalAddress: IDigitalAddress, index: number) => {
                                return (
                                    <tr key={digitalAddress.id}>
                                       { !isFrenchTranslator &&  <td>
                                            <Input type="checkbox"
                                                aria-label={'Select digital address ' + getAddressTxt(digitalAddress)}
                                                checked={checkGroup.isChecked(digitalAddress.id)}
                                                onChange={event => checkGroup.onCheck(event, digitalAddress.id)}
                                                disabled={!!org.details?.shareLinkId}
                                            />
                                        </td>}
                                        <td>{digitalAddress.digitalType ? digitalAddress.digitalType.label : "No Type"}</td>
                                        <td>{getAddressTxt(digitalAddress)}</td>
                                        { !isFrenchTranslator &&<td>
                                            {digitalAddress.assignType.code === "PRIMARY"
                                                ? <CheckMarkIcon></CheckMarkIcon>
                                                : <Input type="checkbox"
                                                    aria-label={`Set primary ${digitalAddress.digitalType.label}`}
                                                    checked={digitalAddress.assignType.code === "PRIMARY"}
                                                    onChange={event=>setPrimary(digitalAddress)}
                                                    disabled={!!org.details?.shareLinkId}/>
                                            }
                                        </td>}
                                        {isFrenchTranslator &&<td>
                                            {digitalAddress.assignType.code === "PRIMARY"
                                                ? <CheckMarkIcon></CheckMarkIcon>
                                                : <CancelIcon></CancelIcon>}
                                        </td>

                                        }
                                        <td>{digitalAddress.description? digitalAddress.description : "" }
                                         {
                                            digitalAddress.descriptionFrench && frenchState.isFrenchChecked && <><br/><span lang="fr">{ digitalAddress.descriptionFrench  }</span></>  
                                         } 
                                        </td>

                                        { (!isFrenchTranslator || frenchState.isFrenchChecked) && <td>
                                            <ImgButton className="float-end" 
                                                aria-label="Edit digital address" 
                                                title="Edit digital address" 
                                                onClick={()=>editDigitalAddress(digitalAddress)}
                                                disabled={!!org.details?.shareLinkId}>
                                                <EditIcon className="edit-pencil"/>
                                            </ImgButton>
                                        </td>}

                                    </tr>
                                )
                            })
                        }
                    </tbody>
                </Table>
            }

            <DigitalAddressUpdate digitalAddress={digitalAddress} isOpen={editDigitalAddressOpen} toggle={toogleEditDigitalAddress}/>
        </div>
    )
}

export default OrgDigitalAddress
